import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// ============================================================
// Public client-facing intake form.
// Reached via /onboarding/intake/:token — no auth, no app shell.
// Loads the card's pre-filled identity (company, name, email) read-only,
// then lets the client fill in mapping rows matching the Mapping Project
// Brief spreadsheet. Submit replaces all existing rows on the card.
// ============================================================

interface MappingRow {
  projectName: string;
  candidateTitlesExact: string;
  candidateTitlesContains: string;
  candidateBoolean: string;
  hmTitlesExact: string;
  hmTitlesContains: string;
  hmBoolean: string;
  // Location filter — free text plus optional radius in miles. Either
  // pair can be left blank.
  candidateLocation: string;
  candidateRadiusMiles: string;
  companyHqLocation: string;
  companyHqRadiusMiles: string;
  resultsCount: string;
}

const blankRow = (): MappingRow => ({
  projectName: '',
  candidateTitlesExact: '',
  candidateTitlesContains: '',
  candidateBoolean: '',
  hmTitlesExact: '',
  hmTitlesContains: '',
  hmBoolean: '',
  candidateLocation: '',
  candidateRadiusMiles: '',
  companyHqLocation: '',
  companyHqRadiusMiles: '',
  resultsCount: ''
});

interface QuoteInfo {
  sent_at: string;
  approved_at: string | null;
  approved_name: string | null;
  candidate_result_count: number | null;
  company_result_count: number | null;
  estimated_cost_pence: number | null;
}

interface CardInfo {
  company: string | null;
  client_name: string | null;
  email: string | null;
  submitted_at: string | null;
  brief_approved_at: string | null;
  brief_approved_name: string | null;
  // Non-null only after the admin clicks "Send quote" on the internal
  // modal. When present, the page swaps into Quote view.
  quote: QuoteInfo | null;
}

const OnboardingIntake: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [card, setCard] = useState<CardInfo | null>(null);
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedNow, setSubmittedNow] = useState(false);
  // Approval is opt-in: tick the box, enter the name, then submit acts
  // as both "save" and "approve". Untick and submit = save draft only.
  const [approveChecked, setApproveChecked] = useState(false);
  const [approveName, setApproveName] = useState('');
  // Quote view (rendered when card.quote is non-null) — separate name +
  // submit state so the brief form's state doesn't bleed across.
  const [quoteApproveName, setQuoteApproveName] = useState('');
  const [approvingQuote, setApprovingQuote] = useState(false);
  const [quoteApprovedNow, setQuoteApprovedNow] = useState(false);
  // When viewing a card that's in quote mode, let the client peek at the
  // brief they originally submitted. Defaults to quote view (the action
  // we're asking them for).
  const [showBriefInQuoteMode, setShowBriefInQuoteMode] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/onboarding/intake/${token}`);
      if (res.status === 404) { setError('not_found'); return; }
      if (!res.ok) throw new Error('Failed to load intake form');
      const data = await res.json();
      setCard(data.card);
      // Pre-fill the approval name with the client_name on the card so the
      // common case is one click. Don't pre-tick the box — approval is an
      // explicit action.
      const defaultName = data.card?.brief_approved_name || data.card?.client_name || '';
      if (defaultName) {
        if (!approveName) setApproveName(defaultName);
        if (!quoteApproveName) setQuoteApproveName(defaultName);
      }
      // Hydrate the form: existing rows from prior submissions, or one
      // blank row to start. Each card is intended to capture ONE search;
      // multiple searches are managed as separate onboarding cards by the
      // team (each goes through its own delivery stages).
      if (data.requests && data.requests.length > 0) {
        setRows(data.requests.map((r: any) => ({
          projectName: r.project_name || '',
          candidateTitlesExact: r.candidate_titles_exact || '',
          candidateTitlesContains: r.candidate_titles_contains || '',
          candidateBoolean: r.candidate_boolean || '',
          hmTitlesExact: r.hm_titles_exact || '',
          hmTitlesContains: r.hm_titles_contains || '',
          hmBoolean: r.hm_boolean || '',
          candidateLocation: r.candidate_location || '',
          candidateRadiusMiles: r.candidate_radius_miles != null ? String(r.candidate_radius_miles) : '',
          companyHqLocation: r.company_hq_location || '',
          companyHqRadiusMiles: r.company_hq_radius_miles != null ? String(r.company_hq_radius_miles) : '',
          resultsCount: r.results_count != null ? String(r.results_count) : ''
        })));
      } else {
        setRows([blankRow()]);
      }
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (idx: number, patch: Partial<MappingRow>) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const approveQuote = async () => {
    if (!token) return;
    if (!quoteApproveName.trim()) {
      setError('Please enter your name to approve the quote.');
      return;
    }
    setApprovingQuote(true);
    setError(null);
    try {
      const res = await fetch(`/api/onboarding/intake/${token}/approve-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: quoteApproveName.trim() })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to approve quote');
      }
      setQuoteApprovedNow(true);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setApprovingQuote(false);
    }
  };

  const submit = async () => {
    if (!token) return;
    if (approveChecked && !approveName.trim()) {
      setError('Please enter your name to approve the brief.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: any = { rows };
      if (approveChecked) body.approval = { approve: true, name: approveName.trim() };
      const res = await fetch(`/api/onboarding/intake/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to submit');
      }
      setSubmittedNow(true);
      // Refresh so the success view reflects the saved state.
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center">
          <div className="text-4xl mb-3">🔗</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link not found</h1>
          <p className="text-sm text-gray-600">
            This intake link is no longer valid. It may have been revoked or replaced. Please contact your MarketMapr representative for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  if (quoteApprovedNow) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center">
          <div className="text-4xl mb-3">🎉</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Quote approved</h1>
          <p className="text-sm text-gray-600">
            Thanks{card?.client_name ? `, ${card.client_name.split(' ')[0]}` : ''} — your approval has been recorded. The MarketMapr team will start extracting and enriching your data shortly.
          </p>
        </div>
      </div>
    );
  }

  // Quote view — only when the admin has clicked Send Quote on the card.
  // Clients land here by default; "View brief" toggle lets them double-check
  // what they originally approved.
  if (card?.quote && !showBriefInQuoteMode) {
    const q = card.quote;
    const alreadyApproved = !!q.approved_at;
    const cost = q.estimated_cost_pence != null ? `£${(q.estimated_cost_pence / 100).toFixed(2)}` : '—';
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">MarketMapr</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Your mapping quote</h1>
            <p className="text-sm text-gray-600">
              Based on your approved brief, here's the scope and cost. Approve below to give us the go-ahead to extract and enrich the data.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Company</div>
                <div className="text-sm text-gray-900">{card.company || '—'}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Contact</div>
                <div className="text-sm text-gray-900">{card.client_name || '—'}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Email</div>
                <div className="text-sm text-gray-900">{card.email || '—'}</div>
              </div>
            </div>

            {/* Scope panels — candidate / company / cost. The Sales Nav URLs
                themselves are never shown to the client; only the counts. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
              <div className="bg-green-50/60 border border-green-200 rounded-lg p-4 text-center">
                <div className="text-[11px] uppercase tracking-wider text-green-800 font-semibold mb-1">Candidates</div>
                <div className="text-3xl font-bold text-green-900">
                  {q.candidate_result_count != null ? q.candidate_result_count.toLocaleString() : '—'}
                </div>
                <div className="text-[11px] text-green-800/70 mt-0.5">in scope to map</div>
              </div>
              <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-4 text-center">
                <div className="text-[11px] uppercase tracking-wider text-blue-800 font-semibold mb-1">Companies</div>
                <div className="text-3xl font-bold text-blue-900">
                  {q.company_result_count != null ? q.company_result_count.toLocaleString() : 'TBC'}
                </div>
                <div className="text-[11px] text-blue-800/70 mt-0.5">
                  {q.company_result_count != null ? 'in scope to map (estimate)' : 'confirmed after the candidate map'}
                </div>
              </div>
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 text-center">
                <div className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold mb-1">Estimated cost</div>
                <div className="text-3xl font-bold text-gray-900">{cost}</div>
                <div className="text-[11px] text-gray-600 mt-0.5">one-off</div>
              </div>
            </div>

            <div className="text-[11px] text-gray-500 text-center">
              Quote prepared {new Date(q.sent_at).toLocaleDateString()}
            </div>
          </div>

          {/* View-brief toggle */}
          <div className="text-center mb-4">
            <button
              onClick={() => setShowBriefInQuoteMode(true)}
              className="text-xs text-blue-700 hover:underline"
            >
              View the brief I approved
            </button>
          </div>

          {/* Approval */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            {alreadyApproved ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
                ✓ Quote approved by <strong>{q.approved_name}</strong> on {new Date(q.approved_at!).toLocaleDateString()}. The MarketMapr team has been notified.
              </div>
            ) : (
              <>
                <div className="text-sm text-gray-700 mb-3">
                  Approve this quote to give the MarketMapr team the green light to start extracting and enriching the data.
                </div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Your name (for the audit trail)</label>
                <input
                  value={quoteApproveName}
                  onChange={e => setQuoteApproveName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3"
                />
                {error && error !== 'not_found' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 mb-3">{error}</div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Questions? Email <a href="mailto:alex@marketmapr.com" className="text-blue-700 hover:underline">alex@marketmapr.com</a>
                  </div>
                  <button
                    onClick={approveQuote}
                    disabled={approvingQuote || !quoteApproveName.trim()}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                  >
                    {approvingQuote ? 'Approving…' : 'Approve quote'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (submittedNow) {
    const approved = !!card?.brief_approved_at;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center">
          <div className="text-4xl mb-3">{approved ? '🎉' : '✅'}</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {approved ? 'Brief approved' : 'Brief saved'}
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            Thanks{card?.client_name ? `, ${card.client_name.split(' ')[0]}` : ''} — {approved
              ? 'your approval has been recorded and the MarketMapr team has been notified to start mapping.'
              : 'your draft brief is saved. Come back to this link any time to finish or approve.'}
          </p>
          <p className="text-xs text-gray-500">
            Need to revise it? You can come back to this link anytime; resubmitting replaces what's on file.
          </p>
          <button
            onClick={() => setSubmittedNow(false)}
            className="mt-4 text-xs text-blue-700 hover:underline"
          >
            Edit your submission
          </button>
        </div>
      </div>
    );
  }

  // The brief view also covers "I want to see the brief I approved while
  // looking at the quote" — same layout, just with a banner pointing back.
  const inBriefFromQuote = !!card?.quote && showBriefInQuoteMode;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {inBriefFromQuote && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center justify-between">
            <div className="text-xs text-blue-900">Viewing your approved brief (read-only). Return to the quote when you're ready to approve.</div>
            <button onClick={() => setShowBriefInQuoteMode(false)} className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap">
              ← Back to quote
            </button>
          </div>
        )}

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">MarketMapr</div>
            {card?.submitted_at && (
              <div className="text-[11px] text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">
                Previously submitted {new Date(card.submitted_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Mapping Project Brief</h1>
          <p className="text-sm text-gray-600">
            Complete one row per search you'd like us to run. List the candidate and hiring-manager titles to map — putting <strong>exact-match</strong> titles and <strong>contains-match</strong> titles in their own columns — and add an optional Boolean string if you want to refine further. Add as many rows as you need.
          </p>
        </div>

        {/* Read-only client identity */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Company</div>
              <div className="text-sm text-gray-900">{card?.company || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Contact</div>
              <div className="text-sm text-gray-900">{card?.client_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Email</div>
              <div className="text-sm text-gray-900">{card?.email || '—'}</div>
            </div>
          </div>
        </div>

        {/* Matching guide */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-xs text-blue-900 space-y-1">
          <div><strong>Exact match</strong> — the words must appear side by side. Enter each title in quotes, e.g. <em>"Sales Director", "Marketing Director"</em>. If you forget the quotes we'll add them for you.</div>
          <div><strong>Contains match</strong> — the keywords can appear anywhere in the title, in any order. e.g. <em>Sales Director</em> also returns 'Sales &amp; Business Development Director' (no quotes needed).</div>
          <div><strong>Tip</strong> — separate multiple titles with a comma. The Boolean column is optional.</div>
        </div>

        {/* Rows */}
        <div className="space-y-4">
          {rows.map((r, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-gray-500">Project</span>
                <input
                  value={r.projectName}
                  onChange={e => updateRow(idx, { projectName: e.target.value })}
                  placeholder="Project name (e.g. Nordics SAP Consultants)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-green-50/40 border border-green-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-green-700 font-semibold">👤 Candidate titles</div>
                  <input
                    value={r.candidateTitlesExact}
                    onChange={e => updateRow(idx, { candidateTitlesExact: e.target.value })}
                    placeholder={'Exact match (e.g. "SAP FICO Consultant", "SAP MM Consultant")'}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                  />
                  <input
                    value={r.candidateTitlesContains}
                    onChange={e => updateRow(idx, { candidateTitlesContains: e.target.value })}
                    placeholder="Contains match (e.g. SAP Consultant, ERP Consultant)"
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                  />
                  <input
                    value={r.candidateBoolean}
                    onChange={e => updateRow(idx, { candidateBoolean: e.target.value })}
                    placeholder='Optional Boolean (e.g. "S/4HANA" NOT Junior)'
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm font-mono"
                  />
                </div>

                <div className="bg-purple-50/40 border border-purple-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-purple-700 font-semibold">👔 Hiring manager titles</div>
                  <input
                    value={r.hmTitlesExact}
                    onChange={e => updateRow(idx, { hmTitlesExact: e.target.value })}
                    placeholder={'Exact match (e.g. "Head of SAP")'}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                  />
                  <input
                    value={r.hmTitlesContains}
                    onChange={e => updateRow(idx, { hmTitlesContains: e.target.value })}
                    placeholder="Contains match (e.g. IT Director, ERP Manager)"
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                  />
                  <input
                    value={r.hmBoolean}
                    onChange={e => updateRow(idx, { hmBoolean: e.target.value })}
                    placeholder="Optional Boolean"
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm font-mono"
                  />
                </div>
              </div>

              {/* Location filter — applies to both candidate-side and
                  company-HQ-side. Either pair can be left blank if
                  there's no geographic constraint. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div className="bg-green-50/40 border border-green-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-green-700 font-semibold">📍 Candidate location</div>
                  <input
                    value={r.candidateLocation}
                    onChange={e => updateRow(idx, { candidateLocation: e.target.value })}
                    placeholder='e.g. United Kingdom, London, or a postcode like "SW1A 1AA"'
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] text-gray-500 whitespace-nowrap">Radius</label>
                    <input
                      type="number"
                      value={r.candidateRadiusMiles}
                      onChange={e => updateRow(idx, { candidateRadiusMiles: e.target.value })}
                      placeholder="optional"
                      className="w-20 px-2 py-1.5 border border-gray-200 rounded text-sm"
                    />
                    <span className="text-[11px] text-gray-500">miles (for postcodes)</span>
                  </div>
                </div>

                <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">📍 Company HQ location</div>
                  <input
                    value={r.companyHqLocation}
                    onChange={e => updateRow(idx, { companyHqLocation: e.target.value })}
                    placeholder='e.g. United Kingdom, London, or a postcode like "SW1A 1AA"'
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] text-gray-500 whitespace-nowrap">Radius</label>
                    <input
                      type="number"
                      value={r.companyHqRadiusMiles}
                      onChange={e => updateRow(idx, { companyHqRadiusMiles: e.target.value })}
                      placeholder="optional"
                      className="w-20 px-2 py-1.5 border border-gray-200 rounded text-sm"
                    />
                    <span className="text-[11px] text-gray-500">miles (for postcodes)</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Expected results</label>
                <input
                  type="number"
                  value={r.resultsCount}
                  onChange={e => updateRow(idx, { resultsCount: e.target.value })}
                  placeholder="e.g. 250"
                  className="w-28 px-2 py-1.5 border border-gray-200 rounded text-sm"
                />
              </div>
            </div>
          ))}

          {/* One search per intake by design — each engagement is tracked
              as its own onboarding card so it can progress through stages
              independently. If the client has multiple briefs, the
              MarketMapr team creates a separate intake link for each. */}
        </div>

        {/* Approval — tick to sign off, blank tick = save draft only */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mt-4">
          {card?.brief_approved_at && (
            <div className="text-[11px] text-green-800 bg-green-50 border border-green-200 rounded px-2 py-1 mb-3 inline-block">
              ✓ Brief previously approved by {card.brief_approved_name || 'client'} on {new Date(card.brief_approved_at).toLocaleDateString()}
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={approveChecked}
              onChange={e => setApproveChecked(e.target.checked)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">I approve this brief — please proceed</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Ticking the box gives the MarketMapr team the go-ahead to start mapping. You can still revise later by visiting this link again.
              </div>
            </div>
          </label>
          {approveChecked && (
            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Your name (for the audit trail)</label>
              <input
                value={approveName}
                onChange={e => setApproveName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          )}
        </div>

        {/* Submit bar */}
        {error && error !== 'not_found' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 mt-4">{error}</div>
        )}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Questions? Email <a href="mailto:alex@marketmapr.com" className="text-blue-700 hover:underline">alex@marketmapr.com</a>
          </div>
          <button
            onClick={submit}
            disabled={submitting}
            className={`px-5 py-2 rounded-lg disabled:opacity-50 font-medium text-white ${approveChecked ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {submitting
              ? (approveChecked ? 'Submitting & approving…' : 'Saving…')
              : (approveChecked ? 'Submit & approve' : (card?.submitted_at ? 'Save changes' : 'Save brief'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingIntake;
